// Tiny WebAudio click — zero-asset UI sound.
// Respects a persisted user mute flag (localStorage `zi_sound_mute`).

let ctx = null;
const getCtx = () => {
  if (typeof window === "undefined") return null;
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    return ctx;
  } catch {
    return null;
  }
};

// Create the (suspended) AudioContext off the tap path. Constructing it on
// the first tap of a session cost ~120 ms of main-thread time on a throttled
// phone profile — a visible hitch on the first drawer toggle or tab switch.
// A context created without a user gesture simply starts suspended; the
// first real tap only has to resume it.
export const warmSound = () => {
  if (isMuted()) return;
  const run = () => { try { getCtx(); } catch {} };
  if (typeof window !== "undefined" && window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 });
  else setTimeout(run, 500);
};

const isMuted = () => {
  try { return localStorage.getItem("zi_sound_mute") === "1"; } catch { return false; }
};

export const setSoundMuted = (m) => {
  try { localStorage.setItem("zi_sound_mute", m ? "1" : "0"); } catch {}
};

export const isSoundMuted = () => isMuted();

// `freq` Hz (pitch), `dur` seconds (length), `gain` peak volume 0–1.
const beep = (freq = 880, dur = 0.06, gain = 0.06, type = "sine") => {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === "suspended") c.resume().catch(() => {});
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, c.currentTime);
    // Pluck envelope: fast attack + decay to avoid clicks
    g.gain.setValueAtTime(0, c.currentTime);
    g.gain.linearRampToValueAtTime(gain, c.currentTime + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
    o.connect(g); g.connect(c.destination);
    o.start(c.currentTime);
    o.stop(c.currentTime + dur + 0.02);
  } catch {}
};

// Soft card-settle "tick" — short, pitched by direction.
export const soundCardSettle = (direction = 0) => {
  // direction: +1 next, -1 prev, 0 initial. Pitch up for next, down for prev.
  const base = direction > 0 ? 980 : direction < 0 ? 720 : 840;
  beep(base, 0.05, 0.045, "triangle");
};

// Snappier tap for openings (double-tap to expand, etc.)
export const soundTap = () => beep(1040, 0.04, 0.04, "sine");

// Subtle tick — used for low-weight interactions like range-tab switches.
export const soundTick = () => beep(1340, 0.022, 0.02, "sine");

// Victory ding — two-note arpeggio played on successful order submission.
// Feels like a premium reward without being obnoxious.
export const soundWin = () => {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  try {
    if (c.state === "suspended") c.resume().catch(() => {});
    const notes = [
      { f: 880,  t: 0.00 },  // A5
      { f: 1318, t: 0.09 },  // E6 — perfect fifth
    ];
    for (const n of notes) {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(n.f, c.currentTime + n.t);
      g.gain.setValueAtTime(0, c.currentTime + n.t);
      g.gain.linearRampToValueAtTime(0.07, c.currentTime + n.t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + n.t + 0.22);
      o.connect(g); g.connect(c.destination);
      o.start(c.currentTime + n.t);
      o.stop(c.currentTime + n.t + 0.24);
    }
  } catch {}
};
